import type { Me } from '@snitch/contract'
import { DAY_FORMS, DEVICE_FORMS, plural } from '@snitch/ui/lib/format'
import { SeatDots } from '@snitch/ui/seat-dots'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  Bell,
  BookOpen,
  ChevronRight,
  FileText,
  KeyRound,
  Languages,
  LogOut,
  MessageCircle,
  Receipt,
  SunMoon,
  TicketPercent,
  Zap,
} from 'lucide-react'
import { useState } from 'react'
import { Avatar } from '../../../components/app-shell.tsx'
import { LoginMethods, PROVIDER_NAMES } from '../../../components/login-methods.tsx'
import { handleLine, ProfileHeader } from '../../../components/profile-header.tsx'
import { Row, Section, Toggle } from '../../../components/settings.tsx'
import { useSupportUnread } from '../../../components/support-link.tsx'
import { THEME_LABELS, ThemeSheet } from '../../../components/theme-sheet.tsx'
import { orpc } from '../../../lib/api.ts'
import { signOut } from '../../../lib/auth.ts'
import { formatDay, heroOf, summaryQuery } from '../../../lib/billing.ts'
import { meQuery } from '../../../lib/session.ts'
import { inTelegram } from '../../../lib/telegram.ts'
import { useThemePreference } from '../../../lib/theme.ts'

export const Route = createFileRoute('/_authed/_shell/profile/')({
  loader: ({ context }) => context.queryClient.ensureQueryData(meQuery),
  component: Profile,
})

/**
 * "Signal / Профиль" on phones (sign-in methods are a sub-screen there); on tablet and desktop
 * the methods sit inline, as in "Профиль · способы входа · ПК".
 */
function Profile() {
  const { data: me } = useSuspenseQuery(meQuery)
  const [themeOpen, setThemeOpen] = useState(false)
  if (!me) return null

  return (
    <>
      {/* Phone / Mini App */}
      <div className="flex flex-col pb-[120px] md:hidden">
        <header className="flex flex-col gap-[18px] px-5 pt-[max(30px,var(--tg-viewport-content-safe-area-inset-top,0px))] pb-2">
          <h1 className="font-bold font-display text-[32px] tracking-[-1.2px]">Профиль</h1>
          <div className="flex items-center gap-3.5">
            <Avatar
              initial={me.user.displayName.charAt(0).toUpperCase()}
              className="size-14 text-[22px]"
            />
            <div className="flex min-w-0 flex-col gap-[3px]">
              <p className="truncate font-semibold text-lg">{me.user.displayName}</p>
              <p className="truncate text-[13px] text-ink-2">{handleLine(me)}</p>
            </div>
          </div>
        </header>
        <div className="flex flex-col gap-[22px] px-5 pt-5 pb-6">
          <SubscriptionCard />
          <Section title="Аккаунт">
            <Row
              icon={KeyRound}
              title="Способы входа"
              sub={me.identities.map((i) => PROVIDER_NAMES[i.provider]).join(' и ')}
              to="/profile/login-methods"
            />
          </Section>
          <SettingsSection me={me} onTheme={() => setThemeOpen(true)} />
          <PaymentSection />
          <HelpSection />
          <SignOutSection />
          <Version me={me} />
        </div>
      </div>

      {/* Tablet / desktop cabinet */}
      <div className="hidden flex-col gap-7 px-10 pt-8 pb-10 md:flex xl:px-14 xl:pt-10">
        <ProfileHeader me={me} />
        <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
          <div className="flex flex-col gap-6">
            <LoginMethods me={me} notice={{}} onNoticeDone={() => {}} />
            <SettingsSection me={me} onTheme={() => setThemeOpen(true)} />
          </div>
          <div className="flex flex-col gap-6">
            <SubscriptionCard />
            <PaymentSection />
            <HelpSection />
            <SignOutSection />
            <Version me={me} />
          </div>
        </div>
      </div>

      <ThemeSheet open={themeOpen} onClose={() => setThemeOpen(false)} />
    </>
  )
}

/** The ink card under the name: what the plan is and until when; taps through to Home. */
function SubscriptionCard() {
  const { data: summary } = useQuery(summaryQuery)
  const hero = summary ? heroOf(summary) : null
  let title = 'Подписки пока нет'
  let sub = 'Тест 7 дней за 75 ₽ или сразу тариф'
  if (hero?.until) {
    if (hero.state === 'expired') {
      title = `Закончилась ${formatDay(hero.until)}`
      sub = 'Вернуть защиту — один тап'
    } else {
      title = `${hero.state === 'trial' ? 'Тест до' : 'Активна до'} ${formatDay(hero.until)}`
      sub = `Осталось ${hero.days} ${plural(hero.days, DAY_FORMS)}`
    }
  }
  return (
    <Link to="/" className="flex items-center gap-3.5 rounded-[26px] surface p-[18px]">
      {hero?.devices ? (
        <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-sunken py-[7px] pr-3 pl-2.5 font-semibold text-[13px] text-ink">
          <SeatDots count={hero.devices} />
          {hero.devices} {plural(hero.devices, DEVICE_FORMS)}
        </span>
      ) : (
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-sunken text-blue">
          <Zap className="size-[18px]" aria-hidden="true" />
        </span>
      )}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="font-semibold text-[15px] tracking-[-0.3px]">{title}</span>
        <span className="text-[13px] text-ink-2">{sub}</span>
      </span>
      <ChevronRight className="size-5 shrink-0 text-ink-2" aria-hidden="true" />
    </Link>
  )
}

function SettingsSection({ me, onTheme }: { me: Me; onTheme: () => void }) {
  const queryClient = useQueryClient()
  const theme = useThemePreference()
  const update = useMutation(
    orpc.account.updateSettings.mutationOptions({
      onMutate: async (input) => {
        // Flip at once; the server answer (or a rollback) follows.
        const previous = queryClient.getQueryData(meQuery.queryKey)
        queryClient.setQueryData(meQuery.queryKey, (old) =>
          old
            ? {
                ...old,
                settings: { notifyExpiry: input.notifyExpiry ?? old.settings.notifyExpiry },
              }
            : old,
        )
        return { previous }
      },
      onError: (_e, _input, ctx) => {
        if (ctx?.previous) queryClient.setQueryData(meQuery.queryKey, ctx.previous)
      },
      onSuccess: (next) => queryClient.setQueryData(meQuery.queryKey, next),
    }),
  )

  return (
    <Section title="Настройки">
      <Row
        icon={SunMoon}
        title="Тема"
        onClick={onTheme}
        trailing={
          <span className="flex items-center gap-1 text-ink-2 text-sm">
            {THEME_LABELS[theme]}
            <ChevronRight className="size-[18px] text-ink-3" aria-hidden="true" />
          </span>
        }
      />
      <Row
        icon={Bell}
        title="Напомнить о конце тарифа"
        sub="За 3 дня и в последний день"
        trailing={
          <Toggle
            label="Напомнить о конце тарифа"
            checked={me.settings.notifyExpiry}
            onChange={(notifyExpiry) => update.mutate({ notifyExpiry })}
          />
        }
      />
      <Row
        icon={Languages}
        title="Язык"
        trailing={<span className="text-ink-2 text-sm">Русский</span>}
      />
    </Section>
  )
}

function PaymentSection() {
  return (
    <Section title="Оплата">
      <Row icon={Receipt} title="История" sub="Заказы и бонусы" to="/profile/history" />
      <Row icon={TicketPercent} title="Промокоды" to="/profile/promos" />
    </Section>
  )
}

function SupportDot() {
  const unread = useSupportUnread()
  return unread ? (
    <span className="flex items-center gap-2">
      <span className="rounded-full bg-red px-2 py-0.5 font-bold font-mono text-[#fff] text-[11px]">
        {unread}
      </span>
      <ChevronRight className="size-5 text-ink-3" aria-hidden="true" />
    </span>
  ) : (
    <ChevronRight className="size-5 text-ink-3" aria-hidden="true" />
  )
}

function HelpSection() {
  return (
    <Section title="Помощь">
      <Row
        icon={MessageCircle}
        title="Поддержка"
        sub="ИИ и живой оператор"
        to="/support"
        trailing={<SupportDot />}
      />
      <Row icon={BookOpen} title="Как подключиться" sub="HAPP или INCY — 2 минуты" soon />
      <Row icon={FileText} title="Документы" sub="Оферта и конфиденциальность" soon />
    </Section>
  )
}

/** Websites only: inside Telegram the session is the Telegram account itself. */
function SignOutSection() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  if (inTelegram) return null
  return (
    <ul className="rounded-[22px] border border-line bg-card px-[18px] py-1">
      <Row
        icon={LogOut}
        tone="danger"
        title="Выйти"
        sub="Только на этом устройстве"
        onClick={async () => {
          await signOut()
          queryClient.setQueryData(meQuery.queryKey, null)
          await navigate({ to: '/login', replace: true })
        }}
      />
    </ul>
  )
}

function Version({ me }: { me: Me }) {
  return <p className="font-mono text-[11px] text-ink-3">Snitch · ID {me.user.id.slice(0, 8)}</p>
}
